import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const LoginForm = ({ language }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  const mockCredentials = {
    username: 'admin@africcoffee.com',
    password: 'Admin@2025'
  };

  const translations = {
    en: {
      title: 'Admin Login',
      subtitle: 'Access your administrative dashboard',
      username: 'Username',
      usernamePlaceholder: 'Enter your username',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      rememberMe: 'Remember me for 30 days',
      forgotPassword: 'Forgot password?',
      loginButton: 'Sign In',
      loggingIn: 'Signing in...',
      securityNote: 'Your session is secured with enterprise-grade encryption',
      invalidCredentials: 'Invalid username or password. Please try again.',
      accountLocked: 'Account temporarily locked due to multiple failed attempts. Please try again in 15 minutes.',
      usernameRequired: 'Username is required',
      passwordRequired: 'Password is required',
      mockCredentialsTitle: 'Demo Credentials',
      mockUsername: 'Username: admin@africcoffee.com',
      mockPassword: 'Password: Admin@2025'
    },
    zh: {
      title: '管理员登录',
      subtitle: '访问您的管理仪表板',
      username: '用户名',
      usernamePlaceholder: '请输入用户名',
      password: '密码',
      passwordPlaceholder: '请输入密码',
      rememberMe: '记住我30天',
      forgotPassword: '忘记密码？',
      loginButton: '登录',
      loggingIn: '登录中...',
      securityNote: '您的会话受企业级加密保护',
      invalidCredentials: '用户名或密码无效。请重试。',
      accountLocked: '由于多次失败尝试，账户暂时锁定。请在15分钟后重试。',
      usernameRequired: '用户名为必填项',
      passwordRequired: '密码为必填项',
      mockCredentialsTitle: '演示凭据',
      mockUsername: '用户名：admin@africcoffee.com',
      mockPassword: '密码：Admin@2025'
    }
  };

  const t = translations?.[language];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.username?.trim()) {
      newErrors.username = t?.usernameRequired;
    }

    if (!formData?.password?.trim()) {
      newErrors.password = t?.passwordRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    setLoginError('');
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      rememberMe: e?.target?.checked
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Clear any existing auth state to prevent conflicts
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('rememberAdmin');
    
    setLoginError('');
    
    if (failedAttempts >= 5) {
      setLoginError(t?.accountLocked);
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // TEMPORARY: Mock admin login for local testing
      // Bypass Supabase authentication entirely
      const inputUsername = formData.username?.trim();
      const inputPassword = formData.password?.trim();
      
      // Simple bypass: accept admin credentials with any variation
      const isAdminEmail = inputUsername?.toLowerCase()?.includes('admin') && inputUsername?.includes('@');
      const isAdminPassword = inputPassword === 'Admin@2025';
      
      console.log('Checking credentials:', {
        input: { username: inputUsername, password: inputPassword },
        isAdminEmail,
        isAdminPassword,
        usernameLength: inputUsername?.length,
        passwordLength: inputPassword?.length
      });
      
      if (isAdminEmail && isAdminPassword) {
        console.log('Mock admin login successful - bypassing Supabase');
        localStorage.setItem('isAdminAuthenticated', 'true');
        if (formData?.rememberMe) {
          localStorage.setItem('rememberAdmin', 'true');
        }
        navigate('/admin-dashboard');
        setIsLoading(false);
        return;
      }

      console.log('Credentials did not match mock, trying Supabase auth...');
      
      // For any other credentials, try real Supabase auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.username,
        password: formData.password,
      });

      console.log('Supabase response:', { data, error });

      if (error) {
        console.error('Login error details:', error);
        setFailedAttempts(prev => prev + 1);
        setLoginError(`Login failed: ${error.message || 'Invalid credentials'}`);
        setIsLoading(false);
        return;
      }

      // Check if user is actually an admin
      const user = data.user;
      const isAdmin = user?.user_metadata?.role === 'admin' ||
        user?.app_metadata?.role === 'admin';

      // Verify profile role as well
      let profileIsAdmin = false;
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profile && profile.role === 'admin') {
          profileIsAdmin = true;
        }
      } catch (err) {
        console.log('Profile check error:', err);
        // Allow login without profile check
        profileIsAdmin = true;
      }

      if (!isAdmin && !profileIsAdmin) {
        await supabase.auth.signOut();
        setLoginError(language === 'en' ? 'Access denied. You do not have admin privileges.' : '拒绝访问。您没有管理员权限。');
        setIsLoading(false);
        return;
      }

      localStorage.setItem('isAdminAuthenticated', 'true');
      if (formData?.rememberMe) {
        localStorage.setItem('rememberAdmin', 'true');
      }
      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setLoginError(language === 'en' ? 'An unexpected error occurred. Please try again.' : '发生意外错误。请重试。');
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="bg-card rounded-lg shadow-card border border-border p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Shield" size={32} color="var(--color-primary)" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
            {t?.title}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t?.subtitle}
          </p>
        </div>

        
        {loginError && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive flex-1">
                {loginError}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label={t?.username}
            type="text"
            name="username"
            placeholder={t?.usernamePlaceholder}
            value={formData?.username}
            onChange={handleInputChange}
            error={errors?.username}
            required
            disabled={isLoading}
          />

          <div className="relative">
            <Input
              label={t?.password}
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder={t?.passwordPlaceholder}
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <Checkbox
              label={t?.rememberMe}
              checked={formData?.rememberMe}
              onChange={handleCheckboxChange}
              disabled={isLoading}
            />
            <button
              type="button"
              className="text-sm text-primary hover:text-primary/80 transition-smooth font-medium"
              disabled={isLoading}
            >
              {t?.forgotPassword}
            </button>
          </div>

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            disabled={isLoading || failedAttempts >= 5}
            iconName="LogIn"
            iconPosition="right"
          >
            {isLoading ? t?.loggingIn : t?.loginButton}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Lock" size={14} />
            <span>{t?.securityNote}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;